"use client";
import { useTranslations } from "@zapier/i18n";

import { Key, Text, Typeahead, TypeaheadItem } from "@zapier/design-system";
import classnames from "classnames";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import styles from "./FilterTypeahead.module.css";

export interface TypeaheadItem {
  id: Key;
  name: string;
  isDisabled?: boolean;
}

function FilterTypeaheadItem({
  item,
  children,
}: {
  item: TypeaheadItem;
  children?: React.ReactNode;
}) {
  return (
    <TypeaheadItem
      id={item.id}
      key={item.id}
      textValue={item.name}
      isDisabled={item.isDisabled}
    >
      <div className={styles.typeaheadItem}>{children || item.name}</div>
    </TypeaheadItem>
  );
}

interface Props {
  autoFocus?: boolean;
  isBusy?: boolean;
  isDisabled?: boolean;
  isLabelHidden?: boolean;
  isRequired?: boolean;
  items: TypeaheadItem[];
  label?: string;
  menuWidth?: string;
  onSearchChange?: (value: string) => void;
  onSelectKey?: (key: Key | null) => void;
  placeholder?: string;
  renderItem?: (item: TypeaheadItem) => React.ReactNode;
  selectedKey?: Key;
  showNoResults?: boolean;
}

export default function FilterTypeahead({
  autoFocus,
  isBusy,
  isDisabled,
  isLabelHidden = false,
  isRequired,
  items,
  label,
  menuWidth,
  onSearchChange,
  onSelectKey,
  placeholder,
  renderItem,
  selectedKey,
  showNoResults,
}: Props) {
  const t = useTranslations("filter-typeahead");

  const [inputValue, setInputValue] = useState<string>("");
  const inputId = useId();

  // Debounce the search change to prevent firing too many requests
  const searchChange = useCallback(
    debounce((value: string) => {
      if (onSearchChange) {
        onSearchChange(value);
      }
    }, 500),
    [onSearchChange],
  );

  // Fired every time the input value changes (on key press)
  const inputChange = (value: string) => {
    if (value !== inputValue) {
      setInputValue(value);
      searchChange(value);
    }
  };

  // Fired when an item is selected from the dropdown
  const selectionChange = (key: Key | null) => {
    if (onSelectKey) {
      onSelectKey(key);
    }
    const value = items.find((item) => item.id === selectedKey)?.name;
    setInputValue(value || "");
  };

  // Make sure the input value always reflect the selected item
  useEffect(() => {
    const value = items.find((item) => item.id === selectedKey)?.name;
    setInputValue(value || "");
  }, [selectedKey]);

  const inputReference = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) {
      // Focus on input on render
      inputReference.current?.focus();
    }
  }, []);

  return (
    <div className={styles.root}>
      <label
        className={classnames(styles.label, {
          [styles.hiddenLabel]: isLabelHidden,
        })}
        htmlFor={inputId}
      >
        {label}
        {isRequired && (
          <>
            {" "}
            {/* eslint-disable-next-line react/jsx-no-literals */}
            <abbr>*</abbr> <Text>{t("labels.required")}</Text>
          </>
        )}
      </label>
      <Typeahead
        aria-label={label}
        defaultInputValue={inputValue || undefined}
        // eslint-disable-next-line unicorn/no-null -- Using `undefined` falls back to `navSearch`. `null` is intentional.
        iconLeft={isBusy ? "navSearch" : null}
        iconRight="arrowDropdown"
        id={inputId}
        isBusy={isBusy}
        isDisabled={isDisabled}
        items={items}
        menuTrigger={autoFocus ? undefined : "focus"} // Don't show the menu on focus if autoFocus is true
        menuWidth={menuWidth || undefined} // Default to full typeahead width if menuWidth is not provided
        onInputChange={inputChange}
        onChange={selectionChange}
        placeholder={placeholder}
        ref={inputReference}
        selectedKey={selectedKey}
        size="medium"
      >
        {!isBusy && items.length === 0 && !!showNoResults ? (
          <FilterTypeaheadItem
            item={{ id: "no-results", name: "No results", isDisabled: true }}
          >
            {t("messages.no-results")}
          </FilterTypeaheadItem>
        ) : (
          (item) => (
            <FilterTypeaheadItem item={item}>
              {renderItem ? renderItem(item) : undefined}
            </FilterTypeaheadItem>
          )
        )}
      </Typeahead>
    </div>
  );
}
